// src/pages/AgriKnowledgePage.jsx
import React, { useState } from "react";
import {
  Btn, Modal, SectionHeader, EmptyState, InfoBanner,
  Input, Select, FileUpload, ProgressBar, Tabs, Badge,
} from "../components/UI";
import { uploadFile, deleteFile } from "../utils/uploadFile";
import { addDocument, deleteDocument } from "../hooks/useFirestore";

const CATEGORY_OPTIONS = [
  "Crop Management", "Soil Science", "Irrigation", "Pest Control",
  "Organic Farming", "Market Prices", "Government Schemes", "Weather",
  "Livestock", "Technology", "General",
];

function AgriKnowledgeForm({ onSave, onClose }) {
  const [tab, setTab] = useState("youtube");
  const [form, setForm] = useState({
    title: "",
    contributorBy: "",
    date: new Date().toISOString().split("T")[0],
    category: "",
    thumbnailFile: null,
    thumbnailPreview: "",
    youtubeLink: "",
    videoFile: null,
    videoName: "",
  });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (!form.contributorBy.trim()) { setError("Contributor name is required."); return; }
    if (tab === "youtube" && !form.youtubeLink.trim()) { setError("YouTube link is required."); return; }
    if (tab === "upload" && !form.videoFile) { setError("Please select a video file."); return; }
    setError(""); setUploading(true);

    try {
      let thumbnailURL = "";
      if (form.thumbnailFile) {
        thumbnailURL = await uploadFile(form.thumbnailFile, "agriknowledge/thumbnails", p => setProgress(p * 0.35));
      }

      let videoURL = "";
      if (tab === "upload" && form.videoFile) {
        videoURL = await uploadFile(form.videoFile, "agriknowledge/videos", p => setProgress(35 + p * 0.65));
      }

      setProgress(100);
      await addDocument("agriknowledge", {
        title: form.title.trim(),
        contributorBy: form.contributorBy.trim(),
        date: form.date,
        category: form.category,
        thumbnailURL,
        type: tab,
        youtubeLink: tab === "youtube" ? form.youtubeLink.trim() : "",
        videoURL: tab === "upload" ? videoURL : "",
      });

      onSave();
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please check your Firebase config.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {error && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
          padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#c53030",
        }}>{error}</div>
      )}
      {uploading && <ProgressBar value={progress} label="Uploading..." />}

      <Tabs
        tabs={[
          { value: "youtube", label: "YouTube Link", icon: "▶" },
          { value: "upload", label: "Upload Video", icon: "📤" },
        ]}
        active={tab}
        onChange={setTab}
      />

      <Input label="Video Title" value={form.title} onChange={v => set("title", v)} required placeholder="Enter video title..." />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Input label="Contributor / By" value={form.contributorBy} onChange={v => set("contributorBy", v)} required placeholder="e.g. Dr. Ramesh Patil" />
        <Input label="Date" type="date" value={form.date} onChange={v => set("date", v)} required />
      </div>

      <Select label="Category" value={form.category} onChange={v => set("category", v)} options={CATEGORY_OPTIONS} />

      <FileUpload
        label="Thumbnail Image"
        accept="image/*"
        onFile={f => { set("thumbnailFile", f); set("thumbnailPreview", URL.createObjectURL(f)); }}
        preview={form.thumbnailPreview}
        hint="Video thumbnail — JPG, PNG (recommended 1280×720px)"
      />

      {tab === "youtube" ? (
        <Input
          label="YouTube Link"
          value={form.youtubeLink}
          onChange={v => set("youtubeLink", v)}
          required
          placeholder="https://www.youtube.com/watch?v=..."
        />
      ) : (
        <FileUpload
          label="Upload Video File"
          accept="video/*"
          required
          onFile={f => { set("videoFile", f); set("videoName", f.name); }}
          hint={form.videoName ? `✓ ${form.videoName}` : "MP4 recommended — file stored in Firebase Storage"}
        />
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 14, borderTop: "1px solid #eee" }}>
        <Btn onClick={onClose} variant="secondary" disabled={uploading}>Cancel</Btn>
        <Btn onClick={handleSave} disabled={uploading}>🌱 Save Video</Btn>
      </div>
    </div>
  );
}

export default function AgriKnowledgePage({ agriVideos, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? agriVideos : agriVideos.filter(v => v.type === filter);

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      if (item.thumbnailURL) await deleteFile(item.thumbnailURL);
      if (item.videoURL) await deleteFile(item.videoURL);
      await deleteDocument("agriknowledge", item.id);
    } catch (err) {
      alert("Delete failed. Check console.");
    }
  };

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return match ? match[1] : null;
  };

  return (
    <div>
      {!isAdmin && <InfoBanner type="warning">👁 View-only mode.</InfoBanner>}

      <SectionHeader
        title="AgriKnowledge Videos"
        action={isAdmin && <Btn onClick={() => setShowForm(true)}>+ Add Video</Btn>}
      />

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {["all", "youtube", "upload"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "5px 14px", borderRadius: 99, fontSize: 12, fontWeight: 600,
            border: `1px solid ${filter === f ? "#2d7a3a" : "#ddd"}`,
            background: filter === f ? "#e8f5e9" : "#fff",
            color: filter === f ? "#2d7a3a" : "#666",
            cursor: "pointer", fontFamily: "inherit",
          }}>
            {f === "all" ? `All (${agriVideos.length})`
              : f === "youtube" ? `▶ YouTube (${agriVideos.filter(v => v.type === "youtube").length})`
              : `📤 Uploaded (${agriVideos.filter(v => v.type === "upload").length})`}
          </button>
        ))}
      </div>

      {/* Video list */}
      <div style={{ display: "grid", gap: 12 }}>
        {filtered.map(v => {
          const ytId = v.type === "youtube" ? getYouTubeId(v.youtubeLink) : null;
          const ytThumb = ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg` : null;

          return (
            <div key={v.id} style={{
              background: "#fff", border: "1px solid #e8f0e9", borderRadius: 12,
              padding: 16, display: "flex", gap: 14, alignItems: "flex-start",
            }}>
              {/* Thumbnail */}
              <div style={{
                width: 110, height: 70, borderRadius: 8, overflow: "hidden", flexShrink: 0,
                background: v.type === "youtube" ? "#ff000022" : "#1a1a2e22",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {(v.thumbnailURL || ytThumb) ? (
                  <img
                    src={v.thumbnailURL || ytThumb}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ fontSize: 28 }}>{v.type === "youtube" ? "▶" : "🎞"}</span>
                )}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>{v.title}</span>
                  <Badge color={v.type === "youtube" ? "#c53030" : "#2b6cb0"}>
                    {v.type === "youtube" ? "▶ YouTube" : "📤 Uploaded"}
                  </Badge>
                  {v.category && <Badge color="#6b46c1">{v.category}</Badge>}
                </div>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                  👤 {v.contributorBy} · 📅 {v.date}
                </div>
                {v.youtubeLink && (
                  <a href={v.youtubeLink} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, color: "#2d7a3a", fontWeight: 600 }}>
                    ▶ Watch on YouTube →
                  </a>
                )}
                {v.videoURL && (
                  <a href={v.videoURL} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, color: "#2b6cb0", fontWeight: 600 }}>
                    🎬 View Video →
                  </a>
                )}
              </div>

              {isAdmin && (
                <Btn variant="danger" style={{ fontSize: 12, padding: "6px 12px", flexShrink: 0 }}
                  onClick={() => handleDelete(v)}>🗑 Delete</Btn>
              )}
            </div>
          );
        })}

        {!filtered.length && (
          <EmptyState
            icon="🌱"
            message={isAdmin ? "No videos yet. Click '+ Add Video' to add one." : "No videos available."}
          />
        )}
      </div>

      {showForm && (
        <Modal title="Add AgriKnowledge Video" onClose={() => setShowForm(false)}>
          <AgriKnowledgeForm onSave={() => setShowForm(false)} onClose={() => setShowForm(false)} />
        </Modal>
      )}
    </div>
  );
}
